// One time field, everywhere. You type the time.
//
// This was three dropdowns — hour, minute, AM/PM — which is three decisions for
// something the person already knows. Now they type "100" and it means one in
// the afternoon. See lib/timeInput for everything it accepts and how the AM/PM
// guess works.
//
// Two rules make a guessing field safe to use:
//
//   It echoes.       The moment the text parses, the field rewrites itself as
//                    "1:00 PM". A guess you can't see is a guess you can't
//                    correct, and this one is correctable by typing "a".
//   It never lies.   Text it can't read is left exactly as typed, with the
//                    reason under it, and nothing is committed. It does not
//                    fall back to a default and it does not silently revert —
//                    a time nobody chose is worse than an empty field.
//
// Value in and out stays 24-hour "HH:MM", which is what the API and the
// database use, so every caller is unchanged.

import { useEffect, useState } from 'react';
import { parseTimeInput, formatTimeValue, timeSuggestions } from '../../lib/timeInput';

// Rendered once into a shared <datalist>, so the browser offers quarter hours
// without any of them being required — the field still takes 9:20.
const SUGGESTIONS = timeSuggestions();

export { parseTimeInput, formatTimeValue };

/**
 * @param value    "HH:MM" 24-hour, or '' for none
 * @param onChange called with "HH:MM" 24-hour
 * @param label    used for the aria-label, e.g. "Start time"
 */
export default function TimeSelect({ value, onChange, disabled = false, label = 'Time', id }) {
  const [text, setText] = useState(() => formatTimeValue(value));
  const [bad, setBad] = useState(false);

  // Follow the value when it changes underneath — a form resetting, or the day
  // dialog loading its hours — but not while the field is being typed in.
  useEffect(() => {
    setText(formatTimeValue(value));
    setBad(false);
  }, [value]);

  function commit(next) {
    const parsed = parseTimeInput(next);
    if (parsed) {
      setBad(false);
      setText(formatTimeValue(parsed));
      if (parsed !== value) onChange(parsed);
      return;
    }
    // Empty is a legitimate answer where the caller allows one; anything else
    // that won't parse stays on screen with the reason.
    if (!next.trim()) {
      setBad(false);
      setText('');
      if (value) onChange('');
      return;
    }
    setBad(true);
  }

  return (
    <div>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        list="stallside-times"
        // Wide enough for the longest time it can hold. At w-28 a two-digit
        // hour with a meridiem — "10:00 AM" — ran under the right-hand edge,
        // and a field that clips the value it is showing you is worse than one
        // that takes a little more room.
        className={'input w-32 ' + (bad ? 'border-rose-400 focus:border-rose-500' : '')}
        aria-label={label}
        aria-invalid={bad || undefined}
        placeholder="1:00 PM"
        autoComplete="off"
        disabled={disabled}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (bad) setBad(false);
        }}
        // Select-all on focus: the field always holds a complete time, so
        // typing should replace it rather than land in the middle of it.
        onFocus={(e) => e.target.select()}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(e.currentTarget.value);
          }
        }}
      />
      {bad && (
        <p className="text-xs text-rose-600 mt-1">
          Try 3:30, 330, or 3:30 pm.
        </p>
      )}
      <datalist id="stallside-times">
        {SUGGESTIONS.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
    </div>
  );
}
